import React, { ComponentProps, useCallback, useState } from 'react'
import { WebTarget } from 'styled-components'

import {
  Button,
  MediaAsContents,
  OpenVideoModalTextButton,
  isUppercase,
  mdEndPixels,
  pageLayoutMaxWidthPixels,
} from '@syconium/little-miss-figgy'

import { MaxIterationsVideo } from '../../../app/_components/media/MaxIterationsVideo.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { VideoModal } from '../../../brunswick/components/VideoModal'
import { VimeoVideo } from '../../../brunswick/components/VimeoVideo'
import { usePageSectionContext } from '../../../components/page-sections/PageSectionContext'
import { MaybeSpaLinkWrapper } from '../../../lib/adapters/next-routing-service/MaybeSpaLinkWrapper'
import { trackEvent } from '../../../lib/analytics'
import { useImageLoader } from '../../../lib/hooks/useImageLoader'
import { useResponsiveVideoSrc } from '../../../lib/hooks/useResponsiveVideoSrc'
import { PlayButton } from '../../../lib/shared-styles'
import { PageHeroSection as Props } from '../../../types/graphql/contentful-components/page-hero-section'
import { InlineVideoPlatforms } from '../../../types/video'

import { HeroImage } from './HeroImage'
import {
  Container,
  Eyebrow,
  HeroBottomMargin,
  ImageWrap,
  ImageWrapOverlay,
  MobileAssetOverlayWhenBelow,
  MobileBelowTitleImage,
  PlayButtonWrapper,
  StyledButtonBar,
  StyledButtonList,
  StyledButtonListItem,
  StyledScrollDownArrow,
  Subtitle,
  TextBelowSection,
  TextWrap,
  TextWrapPlayButton,
  TextWrapPlayButtonMobile,
  TextWrapTitleImage,
  Title,
  TopArrowSpacer,
  VimeoContainer,
} from './styles'
import { usePlayPrimaryVideo } from './usePlayPrimaryVideo'

const category = 'page-hero-section'

type PageHeroSectionProps = Props & {
  overrideVideoPlayState?: ComponentProps<typeof VimeoVideo>['overridePlayState']
  forwardedAs?: WebTarget
  className?: string
}

export const PageHeroSection = ({
  backgroundColor,
  buttonVariantDesktop,
  buttonVariantMobile,
  description,
  eyebrow,
  hideDownArrow,
  imageDesktop,
  imageMobile,
  inlineVideoDesktop,
  inlineVideoMobile,
  inlineVimeoIdDesktop,
  inlineVimeoIdMobile,
  isCriticalImage,
  isTitleItalic,
  isTitleSerif,
  linkHref1,
  linkHref2,
  linkText1,
  linkText2,
  modalVideoId,
  modalVideoIdMobile,
  modalVideoPlatform,
  modalVideoPlatformMobile,
  name,
  overlayColor,
  subtitle,
  mobileTitleColor,
  desktopTitleColor,
  textContentMaxWidthDesktop,
  textContentMaxWidthMobile,
  textPlacementDesktop,
  textPlacementMobile,
  title: propsTitle,
  titleImage,
  titleImageAspectRatio,
  titleImageMaxWidthDesktop,
  titleImageMaxWidthMobile,
  titleImageMobile,
  titleImageMobileAspectRatio,
  isFullscreenHeight,
  hasVerticalMargin,
  openVideoModalText,
  overrideVideoPlayState,
  forwardedAs,
  className,
}: PageHeroSectionProps) => {
  const { contentfulImageLoader } = useImageLoader()
  const hasModalVideo = Boolean(modalVideoId && modalVideoPlatform)
  const hasMobileModalVideo = Boolean(modalVideoIdMobile && modalVideoPlatformMobile)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [isMobileVideoModalOpen, setIsMobileVideoModalOpen] = useState(false)

  const { pageSectionIndex: pageSectionIndex } = usePageSectionContext()

  const {
    magnolia: { general },
    pageSections: {
      pageHeroSection: { playVideo },
    },
  } = useTranslation()
  const defaultButtonVariant = 'black-on-white-no-border'

  const title = propsTitle ?? ''

  const altText = (title || description) ?? ''

  const changeVideoModalState = (isMobile: boolean, isOpen: boolean) => {
    if (isMobile && hasMobileModalVideo) {
      setIsMobileVideoModalOpen(isOpen)
    } else if (hasModalVideo) {
      setIsVideoModalOpen(isOpen)
    }
  }

  const openVideoModal = useCallback(
    (isMobile: boolean) => {
      changeVideoModalState(isMobile, true)
    },
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasModalVideo, hasMobileModalVideo]
  )

  const closeVideoModal = useCallback((isMobile: boolean) => {
    changeVideoModalState(isMobile, false)
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const mobileOrDefaultTitleImage = titleImageMobile || titleImage

  const video = useResponsiveVideoSrc({
    inlineVideoDesktop,
    inlineVideoMobile,
    inlineVimeoIdDesktop,
    inlineVimeoIdMobile,
  })

  const imageSrc = imageDesktop ?? imageMobile ?? undefined

  const { markPrimaryVideoAsPlayed, playPrimaryVideo, primaryVideoRef } = usePlayPrimaryVideo()
  const computedButtonVariantMobile = buttonVariantMobile ?? defaultButtonVariant
  const computedButtonVariantDesktop = buttonVariantDesktop ?? defaultButtonVariant
  const HeroText = (
    <TextWrap
      $mobileTextColor={mobileTitleColor}
      $desktopTextColor={desktopTitleColor}
      $textPlacementMobile={textPlacementMobile}
      $textPlacementDesktop={textPlacementDesktop}
      $textContentMaxWidthDesktop={textContentMaxWidthDesktop}
      $textContentMaxWidthMobile={textContentMaxWidthMobile}
      onClick={playPrimaryVideo}
    >
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      {titleImage && (
        <TextWrapTitleImage
          titleImageMaxWidthDesktop={titleImageMaxWidthDesktop}
          titleImageMaxWidthMobile={titleImageMaxWidthMobile}
          alt={altText}
          src={titleImage}
          srcs={
            titleImageMobile
              ? {
                  sm: titleImageMobile,
                  md: titleImage,
                }
              : undefined
          }
          loading={isCriticalImage ? 'default' : 'lazy'}
          aspectRatios={{
            sm: titleImageMobile ? titleImageMobileAspectRatio : titleImageAspectRatio,
            md: titleImageAspectRatio,
          }}
          widths={{
            unit: 'px',
            sm: titleImageMaxWidthMobile ?? mdEndPixels,
            md: titleImageMaxWidthDesktop ?? pageLayoutMaxWidthPixels,
          }}
          transparentBackground
        />
      )}
      {title && (
        <Title {...{ isTitleSerif, isTitleItalic }} asTag='h3' isUppercase={isUppercase(title)}>
          {title}
        </Title>
      )}
      {subtitle && <Subtitle isUppercase={isUppercase(subtitle)}>{subtitle}</Subtitle>}
      {hasModalVideo &&
        (!openVideoModalText ? (
          <TextWrapPlayButton
            aria-label={playVideo}
            onClick={() => openVideoModal(false)}
            {...{ hasMobileModalVideo }}
            {...trackEvent({
              category,
              action: 'open video modal',
              pageSectionName: title ?? name,
              contentfulName: name ?? undefined,
              pageSectionIndex,
              correspondingAsset: titleImage ?? imageDesktop ?? undefined,
            })}
          />
        ) : (
          <MediaAsContents greaterThanOrEqual='md'>
            <PlayButtonWrapper>
              <OpenVideoModalTextButton
                onClick={() => openVideoModal(false)}
                text={openVideoModalText}
                {...trackEvent({
                  category,
                  action: 'open video modal',
                  pageSectionName: title ?? name,
                  contentfulName: name ?? undefined,
                  pageSectionIndex,
                  correspondingAsset: titleImage ?? imageDesktop ?? undefined,
                })}
              />
            </PlayButtonWrapper>
          </MediaAsContents>
        ))}
      {hasMobileModalVideo &&
        (!openVideoModalText ? (
          <TextWrapPlayButtonMobile
            aria-label={playVideo}
            onClick={() => openVideoModal(true)}
            {...trackEvent({
              category,
              action: 'open video modal',
              pageSectionName: title ?? name,
              contentfulName: name ?? undefined,
              pageSectionIndex,
              correspondingAsset: titleImage ?? imageDesktop ?? undefined,
            })}
          />
        ) : (
          <MediaAsContents lessThan={'md'}>
            <PlayButtonWrapper>
              <OpenVideoModalTextButton
                onClick={() => openVideoModal(true)}
                text={openVideoModalText}
                {...trackEvent({
                  category,
                  action: 'open video modal',
                  pageSectionName: title ?? name,
                  contentfulName: name ?? undefined,
                  pageSectionIndex,
                  correspondingAsset: titleImage ?? imageDesktop ?? undefined,
                })}
              />
            </PlayButtonWrapper>
          </MediaAsContents>
        ))}
      <StyledButtonBar>
        <StyledButtonList role='list'>
          <MediaAsContents lessThan='md'>
            <StyledButtonListItem role='listitem'>
              <MaybeSpaLinkWrapper href={linkHref1}>
                <Button
                  as='a'
                  href={linkHref1}
                  variant={computedButtonVariantMobile}
                  {...trackEvent({
                    category,
                    action: 'click cta 1',
                    label: linkText1,
                    pageSectionName: title ?? name,
                    contentfulName: name ?? undefined,
                    pageSectionIndex,
                    correspondingAsset: titleImage ?? imageDesktop ?? undefined,
                  })}
                >
                  {linkText1}
                </Button>
              </MaybeSpaLinkWrapper>
            </StyledButtonListItem>
            {linkText2 && linkHref2 && (
              <StyledButtonListItem role='listitem'>
                <MaybeSpaLinkWrapper href={linkHref2}>
                  <Button
                    as='a'
                    href={linkHref2}
                    variant={computedButtonVariantMobile}
                    {...trackEvent({
                      category,
                      action: 'click cta 2',
                      label: linkText2,
                      pageSectionName: title ?? name,
                      contentfulName: name ?? undefined,
                      pageSectionIndex,
                      correspondingAsset: titleImage ?? imageDesktop ?? undefined,
                    })}
                  >
                    {linkText2}
                  </Button>
                </MaybeSpaLinkWrapper>
              </StyledButtonListItem>
            )}
          </MediaAsContents>
          <MediaAsContents greaterThanOrEqual='md'>
            <StyledButtonListItem role='listitem'>
              <MaybeSpaLinkWrapper href={linkHref1}>
                <Button
                  as='a'
                  href={linkHref1}
                  variant={computedButtonVariantDesktop}
                  {...trackEvent({
                    category,
                    action: 'click cta 1',
                    label: linkText1,
                    pageSectionName: title ?? name,
                    contentfulName: name ?? undefined,
                    pageSectionIndex,
                    correspondingAsset: titleImage ?? imageDesktop ?? undefined,
                  })}
                >
                  {linkText1}
                </Button>
              </MaybeSpaLinkWrapper>
            </StyledButtonListItem>
            {linkText2 && linkHref2 && (
              <StyledButtonListItem role='listitem'>
                <MaybeSpaLinkWrapper href={linkHref2}>
                  <Button
                    as='a'
                    href={linkHref2}
                    variant={computedButtonVariantDesktop}
                    {...trackEvent({
                      category,
                      action: 'click cta 2',
                      label: linkText2,
                      pageSectionName: title ?? name,
                      contentfulName: name ?? undefined,
                      pageSectionIndex,
                      correspondingAsset: titleImage ?? imageDesktop ?? undefined,
                    })}
                  >
                    {linkText2}
                  </Button>
                </MaybeSpaLinkWrapper>
              </StyledButtonListItem>
            )}
          </MediaAsContents>
        </StyledButtonList>
      </StyledButtonBar>
    </TextWrap>
  )

  return (
    <>
      <Container
        as={forwardedAs}
        className={className}
        isFullscreenHeight={isFullscreenHeight}
        textPlacementMobile={textPlacementMobile}
        data-testid='page-hero-section-container'
      >
        {hideDownArrow !== true ? <TopArrowSpacer /> : null}
        <ImageWrap
          backgroundColor={backgroundColor}
          textPlacementMobile={textPlacementMobile}
          shouldCollapse={!(imageMobile || inlineVideoMobile || hasModalVideo)}
        >
          {imageSrc && !video && (
            <HeroImage
              imageDesktop={imageDesktop}
              imageMobile={imageMobile}
              title={title}
              isCriticalImage={isCriticalImage}
            />
          )}
          {video?.platform === InlineVideoPlatforms.VIMEO ? (
            <VimeoContainer>
              <VimeoVideo
                playText={general.playVideo}
                pauseText={general.pauseVideo}
                autoplay={true}
                includePauseButton={true}
                lazy={!isCriticalImage}
                overridePlayState={overrideVideoPlayState}
                thumbnail={
                  <HeroImage
                    imageDesktop={imageDesktop}
                    imageMobile={imageMobile}
                    title={title}
                    isCriticalImage={isCriticalImage}
                  />
                }
                videoSrc={video.src}
                title={title}
                objectPosition='top'
              />
            </VimeoContainer>
          ) : null}
          {video?.platform === InlineVideoPlatforms.DEFAULT_PLAYER && (
            <MaxIterationsVideo
              autoPlay
              maxIterations={5}
              muted
              onPlay={markPrimaryVideoAsPlayed}
              playsInline
              ref={primaryVideoRef}
              src={video.src}
              aspectRatios={null}
            />
          )}
          <ImageWrapOverlay overlayColor={overlayColor} onClick={playPrimaryVideo} />
          {/** MobileAssetOverlayWhenBelow has display: none. Zero idea why it and its content are here. */}
          <MobileAssetOverlayWhenBelow>
            {mobileOrDefaultTitleImage && (
              <MobileBelowTitleImage
                src={contentfulImageLoader({ src: mobileOrDefaultTitleImage, quality: 90 })}
                {...{ titleImageMaxWidthMobile }}
              />
            )}
            {/** TODO: The open video modal logic is more complex than necessary. And we pass false for isMobile here when that may not be accurate. */}
            {hasModalVideo &&
              (!openVideoModalText ? (
                <PlayButton
                  {...trackEvent({
                    category,
                    action: 'open video modal',
                    pageSectionName: title ?? name,
                    contentfulName: name ?? undefined,
                    pageSectionIndex,
                    correspondingAsset: titleImage ?? imageDesktop ?? undefined,
                  })}
                  aria-label={playVideo}
                  onClick={() => openVideoModal(false)}
                />
              ) : (
                <PlayButtonWrapper>
                  <OpenVideoModalTextButton
                    onClick={() => openVideoModal(false)}
                    text={openVideoModalText}
                    {...trackEvent({
                      category,
                      action: 'open video modal',
                      pageSectionName: title ?? name,
                      contentfulName: name ?? undefined,
                      pageSectionIndex,
                      correspondingAsset: titleImage ?? imageDesktop ?? undefined,
                    })}
                  />
                </PlayButtonWrapper>
              ))}
          </MobileAssetOverlayWhenBelow>
        </ImageWrap>

        <MediaAsContents greaterThanOrEqual={textPlacementMobile === 'below' ? 'md' : 'xs'}>
          {HeroText}
        </MediaAsContents>

        {!hideDownArrow ? (
          <>
            <MediaAsContents lessThan='md'>
              <StyledScrollDownArrow
                hideDownArrow={hideDownArrow}
                textColor={mobileTitleColor}
                textPlacementMobile={textPlacementMobile}
              />
            </MediaAsContents>

            <MediaAsContents greaterThanOrEqual='md'>
              <StyledScrollDownArrow
                hideDownArrow={hideDownArrow}
                textColor={desktopTitleColor}
                textPlacementMobile={textPlacementMobile}
              />
            </MediaAsContents>
          </>
        ) : null}

        {hasModalVideo && (
          <VideoModal
            isOpen={isVideoModalOpen}
            onClose={() => closeVideoModal(false)}
            videoPlatform={modalVideoPlatform}
            videoId={modalVideoId}
          />
        )}

        {hasMobileModalVideo && (
          <VideoModal
            isOpen={isMobileVideoModalOpen}
            onClose={() => closeVideoModal(true)}
            videoPlatform={modalVideoPlatformMobile}
            videoId={modalVideoIdMobile}
          />
        )}
      </Container>

      {textPlacementMobile === 'below' ? (
        <MediaAsContents lessThan='md'>
          <TextBelowSection>{HeroText}</TextBelowSection>
        </MediaAsContents>
      ) : null}
      {hasVerticalMargin && <HeroBottomMargin />}
    </>
  )
}
